import React from "react";
import { type IconTypes, SelectedIcon } from "../../Icons";
import { concatStringFromClassesArrayList, sanitiseString } from "../../Utils";

export interface Props {
  id?: string;
  inputType?: string;
  classes?: string;
  description?: string;
  accessiblePlaceholder?: boolean;
  for?: string;
  attributes?: { [key: string]: string };
  text: string;
  inline?: boolean;
  iconType?: IconTypes;
  dataTestId?: string;
}

export function Label(props: Props) {
  let descriptionID: string;

  const sanitisedId = sanitiseString(props.id);
  const sanitisedDataTestId = sanitiseString(props.dataTestId);

  if (props.id) {
    descriptionID = `${sanitisedId}-description-hint`;
  } else {
    descriptionID = "description-hint";
  }

  const isCheckboxOrRadio =
    props.inputType === "checkbox" || props.inputType === "radio";

  const classList = {
    optionalClasses: [
      !props.inputType ? "ons-label" : "",
      props.classes ? `${props.classes}` : "",
      props.description ? "ons-label--with-description" : "",
      props.accessiblePlaceholder ? "ons-label--placeholder" : "",
    ],
  };

  const classes = concatStringFromClassesArrayList(classList);

  const descriptionSpan = (
    <span
      className={`ons-label__description ${
        props.inputType
          ? `ons-${props.inputType}__label--with-description`
          : "ons-input--with-description"
      }`}
      {...(isCheckboxOrRadio ? {} : { id: descriptionID })}
      data-testid={`${sanitisedDataTestId}-description`}
    >
      {props.description}
    </span>
  );

  return (
    <>
      <label
        className={classes}
        {...(props.for ? { htmlFor: props.for } : {})}
        {...(props.id ? { id: sanitisedId } : {})}
        {...(props.description ? { "aria-describedby": descriptionID } : {})}
        {...(props.attributes
          ? Object.entries(props.attributes)
              .map(([attribute, value]) => ({ [attribute]: value }))
              .reduce((acc, attribute) => ({ ...acc, ...attribute }), {})
          : {})}
        data-testid={`${sanitisedDataTestId}-label`}
      >
        <span>{props.text}</span>
        {props.iconType !== undefined && (
          <span className="ons-u-ml-m">
            <SelectedIcon iconType={props.iconType} />
          </span>
        )}
        {isCheckboxOrRadio && props.description ? (
          <span
            className="ons-label__aria-hidden-description"
            aria-hidden="true"
            data-testid={`${sanitisedDataTestId}-description-checkbox-or-radio-in-label`}
          >
            {descriptionSpan}
          </span>
        ) : (
          ""
        )}
      </label>
      {isCheckboxOrRadio && props.description ? (
        <span
          className="ons-label__visually-hidden-description ons-u-vh"
          id={descriptionID}
          data-testid={`${sanitisedDataTestId}-description-checkbox-or-radio-after-label`}
        >
          {props.description}
        </span>
      ) : (
        ""
      )}
      {!isCheckboxOrRadio && props.description ? descriptionSpan : ""}
    </>
  );
}
